<template>
  <div class="form-field-container">
    <label v-if="showLabel" :for="id" class="form-label">{{ label }}</label>
    <div v-if="showRequired" class="required-mark-container">
      <span v-if="required" class="required-mark">必須</span>
    </div>
    <div class="input-container" :class="`width-${size}`">
      <div class="suggestion-container">
        <input 
          :type="type" 
          :id="id" 
          :name="id" 
          :class="inputClass" 
          :value="modelValue"
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
          :placeholder="placeholder"
          :disabled="disabled"
          :autocomplete="autocomplete"
          :list="enableSuggestions ? id + '-suggestions' : undefined"
          :maxlength="maxlength"
        />
        <datalist v-if="enableSuggestions" :id="id + '-suggestions'">
          <option v-for="(suggestion, index) in suggestions" :key="index" :value="suggestion"></option>
        </datalist>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FormTextField',
  props: {
    label: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    modelValue: {
      type: [String, Number],
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    showRequired: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      default: 'text'
    },
    inputClass: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    size: {
      type: String,
      default: 'large',
      validator: value => ['small', 'medium', 'large'].includes(value)
    },
    maxSuggestions: {
      type: Number,
      default: 5
    },
    enableSuggestions: {
      type: Boolean,
      default: false
    },
    maxlength: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      suggestions: []
    };
  },
  methods: {
    handleInput(event) {
      const value = event.target.value;
      this.$emit('update:modelValue', value);
    },
    
    // フォーカスが外れたときに入力値を保存
    handleBlur() {
      // サジェスチョン機能が無効な場合は何もしない
      if (!this.enableSuggestions) return;
      
      const value = this.modelValue;
      // 入力値が無効な場合は何もしない
      if (!value || value.trim() === '') return;
      
      // 有効な入力値をローカルストレージに保存
      this.saveSuggestion(value);
    },
    handleFocus() {
      // サジェスチョン機能が無効な場合は何もしない
      if (!this.enableSuggestions) return;
      
      // サジェスチョンを読み込む
      this.loadSuggestions();
    },
    saveSuggestion(value) {
      // 既存のサジェスチョンを取得
      const storageKey = `suggestions-${this.id}`;
      let suggestions = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      // 重複を避ける
      if (!suggestions.includes(value)) {
        // 配列の先頭に追加
        suggestions.unshift(value);
        
        // 最大数を超えた場合、古いものを削除
        if (suggestions.length > this.maxSuggestions) {
          suggestions = suggestions.slice(0, this.maxSuggestions);
        }
        
        // ローカルストレージに保存
        localStorage.setItem(storageKey, JSON.stringify(suggestions));
      }
    },
    loadSuggestions() {
      // ローカルストレージからサジェスチョンを読み込む
      const storageKey = `suggestions-${this.id}`;
      this.suggestions = JSON.parse(localStorage.getItem(storageKey) || '[]');
    }
  },
  emits: ['update:modelValue']
}
</script>

<style scoped>
.form-field-container {
  display: flex;
  align-items: center;
  background-color: #FFF;
  padding: 10px 30px;
  margin: 5px;
  border-radius: 4px;
}

.form-label {
  min-width: 180px;
  font-weight: 700;
  flex: 0 0 180px;
}

.required-mark-container {
  width: 40px;
  margin-right: 10px;
  flex-shrink: 0;
}

.required-mark {
  background-color: #ff0000;
  color: white;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 12px;
  width: 100%;
  text-align: center;
  display: inline-block;
}

.input-container {
  min-width: 200px;
}

.input-container.width-small {
  flex: 0 0 200px; /* Small width */
}

.input-container.width-medium {
  flex: 0 0 400px; /* Medium width */
}

.input-container.width-large {
  flex: 1; /* Large width (default) */
}

.suggestion-container {
  position: relative;
  width: 100%;
}

input {
  width: 100%;
  padding: 8px;
  border: 2px solid #195983;
  border-radius: 4px;
  box-sizing: border-box;
}

/* サジェスチョンのスタイル */
datalist {
  position: absolute;
  max-height: 200px;
  overflow-y: auto;
  width: 100%;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 0 0 4px 4px;
  z-index: 10;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}
</style>
